/**
 * The working — what the quantitative bench is for.
 *
 * Under a step's observation, the calculation that observation implies: what
 * went in and how many moles that is, the ratio applied, which reagent ran out,
 * what is left of the others, and how much product there could be.
 *
 * Laid out as it is written BY HAND, not as a table of results. "1.00 g ÷
 * 105.99 g/mol = 9.43 mmol" is the line a student is learning to write, and a
 * panel that reported only the 9.43 would be a calculator — which is the one
 * thing this must not be, since the arithmetic is what they are marked on.
 *
 * Collapsed to its conclusion, because after the first few mixes the
 * conclusion is what you are reading for. Every number comes from the backend's
 * `Stoichiometry`; nothing here computes, and nothing here rounds anything the
 * calculation will use again.
 */
import { useState } from 'react';
import type { Stoichiometry, StoichiometryUnavailable, MoleSource } from '@padcanvas/shared';
import { findSubstance } from '../substanceCatalog';
import { formatChemistry } from '../chemFormat';
import {
  formatMass, formatMoles, formatMolarity, formatMolarMass, formatVolume, plain, sigFigs,
} from '../quantityFormat';

/** What a reader is told when there are no numbers. Each of these is a real
 *  state of the bench, and saying which one it is beats an empty panel. */
const NO_NUMBERS: Record<StoichiometryUnavailable, string> = {
  'no-reaction': 'Nothing reacted here, so there is nothing to work out.',
  'no-equation': 'This reaction has no equation recorded yet, so the amounts cannot be worked through.',
  'unparseable-equation': 'This reaction’s equation could not be read, so nothing has been computed from it.',
  'unbalanced': 'This reaction’s equation does not balance, so nothing has been computed from it.',
  'unknown-substance': 'Something in this vessel is not in the catalogue, so its amount cannot be counted.',
  'unparseable-formula': 'A formula here could not be read, so its moles cannot be worked out.',
  'missing-concentration': 'A solution here has no strength recorded, and a volume on its own is not an amount.',
  'unknown-amount': 'Something here went in without a measured amount, so the ratio cannot be worked out.',
  'reagent-not-in-vessel': 'This reaction uses something that was made along the way, so the amounts cannot be traced from what went in.',
};

/** One species inline — set with the same Unicode subscripts as the equation
 *  above it rather than through KaTeX, so a line of working reads as one
 *  sentence and eight species cost eight string passes rather than eight
 *  typesetting runs. */
function Species({ formula }: { formula: string }): React.JSX.Element {
  return <span className="chemlab-working-species">{formatChemistry(formula)}</span>;
}

function nameOf(substanceId: string): string {
  return findSubstance(substanceId)?.displayName ?? substanceId;
}

/**
 * One reagent's line of arithmetic, in the terms it was actually computed in.
 *
 * What went in is written as it was measured — 3 chips, 10 mL, 1 M — and only
 * what comes out of the division carries three figures. "3.00 × 0.500 g" would
 * claim a measurement nobody made.
 */
function sourceLine(source: MoleSource): string {
  if (source.unit === 'mL' || source.unit === 'drop') {
    const ml = source.unit === 'drop' ? source.qty * 0.05 : source.qty;
    const drops = source.unit === 'drop' ? `${plain(source.qty)} drops = ` : '';
    return `${drops}${plain(ml)} mL × ${formatMolarity(source.concentrationM ?? 0)}`;
  }
  if (source.unit === 'g') {
    return `${plain(source.qty)} g ÷ ${formatMolarMass(source.molarMassGPerMol ?? 0)}`;
  }
  if (source.unit === 'piece') {
    const mass = source.qty * (source.pieceMassG ?? 0);
    return `${plain(source.qty)} × ${plain(source.pieceMassG ?? 0)} g = ${plain(mass)} g`
      + ` ÷ ${formatMolarMass(source.molarMassGPerMol ?? 0)}`;
  }
  // A gas piped in from the vessel next door: it arrived as moles, computed
  // there. There is no measurement to show, and inventing one would be worse.
  return 'carried in as gas';
}

/** The one sentence the block collapses to — and the one the bench's reaction
 *  card shows, so the two can never phrase the same result differently. */
export function conclusionOf(s: Stoichiometry): string {
  const limiting = (s.reagents ?? []).find(r => r.limiting);
  const leftOver = (s.reagents ?? []).filter(r => !r.limiting && r.leftoverMol > 0);
  if (!limiting) return 'Everything was used up.';
  const ran = `${nameOf(limiting.substanceIds[0])} ran out first`;
  if (leftOver.length === 0) return `${ran}, with nothing left over.`;
  const left = leftOver
    .map(r => `${r.leftoverG !== null ? formatMass(r.leftoverG) : formatMoles(r.leftoverMol)} of ${nameOf(r.substanceIds[0])}`)
    .join(' and ');
  return `${ran}. ${left} is left.`;
}

/** A mole count as a table cell: three figures, in the millimoles the caption
 *  names once, rather than repeating "mmol" in every cell. */
function cell(mol: number, sign: '' | '+' | '−' = ''): string {
  if (Math.abs(mol) < 1e-12) return '0';
  return `${sign}${sigFigs(mol * 1000)}`;
}

/**
 * The reaction table — the equation with the moles written under it.
 *
 * The layout a student is taught for exactly this: every species a column, and
 * three rows beneath — what there was at the start, what reacted, what is left.
 * It is where a limiting reagent stops being a word and becomes a zero in the
 * bottom row. Nothing here is computed; every number is the working's own.
 *
 * "Reacts" follows the equation's ratio exactly: one mole of reaction takes
 * each reactant's coefficient and makes each product's, which is why the column
 * of a product and the column of what made it move together.
 */
function ReactionTable({ s }: { s: Stoichiometry }): React.JSX.Element {
  const reagentFor = (formula: string) => s.reagents.find(r => r.formula === formula);
  const productFor = (formula: string) => s.products.find(p => p.formula === formula);
  const marker = (m?: 'gas' | 'precipitate') => (m === 'gas' ? '↑' : m === 'precipitate' ? '↓' : '');

  const columns = [
    ...s.equation.reactants.map((t, i) => {
      const r = reagentFor(t.formula);
      return {
        key: `r${i}`, term: t, side: 'reactant' as const,
        start: r?.mol ?? 0,
        reacts: r ? r.mol - r.leftoverMol : 0,
        end: r?.leftoverMol ?? 0,
        limiting: r?.limiting ?? false,
      };
    }),
    ...s.equation.products.map((t, i) => {
      const p = productFor(t.formula);
      return {
        key: `p${i}`, term: t, side: 'product' as const,
        start: 0, reacts: p?.mol ?? 0, end: p?.mol ?? 0, limiting: false,
      };
    }),
  ];

  const reactantCount = s.equation.reactants.length;

  return (
    <div className="chemlab-reaction-table-wrap">
      <table className="chemlab-reaction-table">
        <thead>
          <tr>
            <th className="chemlab-rt-unit" scope="col">mmol</th>
            {columns.map((c, i) => (
              <th key={c.key} scope="col" className={c.limiting ? 'chemlab-rt-limiting' : undefined}>
                {/* The separator belongs to the column it precedes, so a
                    species and the sign before it stay together when a long
                    equation scrolls. */}
                {i > 0 && <span className="chemlab-rt-sep">{i === reactantCount ? '→' : '+'}</span>}
                {c.term.coefficient > 1 && <span className="chemlab-rt-coef">{c.term.coefficient}</span>}
                <Species formula={c.term.formula} />
                {marker(c.term.marker)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Start</th>
            {columns.map(c => <td key={c.key}>{cell(c.start)}</td>)}
          </tr>
          <tr>
            <th scope="row">Reacts</th>
            {columns.map(c => (
              <td key={c.key}>{cell(c.reacts, c.side === 'reactant' ? '−' : '+')}</td>
            ))}
          </tr>
          <tr className="chemlab-rt-end">
            <th scope="row">End</th>
            {columns.map(c => (
              <td key={c.key} className={c.limiting ? 'chemlab-rt-limiting' : undefined}>{cell(c.end)}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function Working({ stoichiometry, unavailable, expanded = true }: {
  stoichiometry?: Stoichiometry;
  unavailable?: StoichiometryUnavailable;
  /** The step's own More / Less. Collapsed, only the conclusion shows — the
   *  line saying which reagent ran out and what is left. Expanded, the table of
   *  moles and the working behind it. */
  expanded?: boolean;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (!stoichiometry) {
    if (!unavailable) return null;
    // 'no-reaction' is the ordinary case — a dip, a mix where nothing happens
    // — and saying so under every such step would be noise.
    if (unavailable === 'no-reaction') return null;
    return <p className="chemlab-working-none">{NO_NUMBERS[unavailable]}</p>;
  }

  const s = stoichiometry;

  if (!expanded) {
    return (
      <div className="chemlab-working">
        <p className="chemlab-working-head chemlab-working-head-static">
          <span className="chemlab-working-conclusion">{conclusionOf(s)}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="chemlab-working">
      {/* Always shown. The equation alone says what reacts; the moles under it
          say how much, and on this bench that is the whole point. */}
      <ReactionTable s={s} />

      <button className="chemlab-working-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="chemlab-working-conclusion">{conclusionOf(s)}</span>
        <span className="chemlab-working-toggle">{open ? 'Hide the working ▴' : 'Show the working ▾'}</span>
      </button>

      {/* How each number above was reached — the divisions a student writes. */}
      {open && (
        <div className="chemlab-working-body">

          <dl className="chemlab-working-rows">
            <dt>In</dt>
            <dd>
              {/* A backend that predates the derivation sends moles without
                  their sources. Show what it did send: a line short of its
                  working is worth reading, and a bench that crashes is not. */}
              {s.reagents.map(r => ((r.sources ?? []).length > 0
                ? r.sources.map(source => (
                  <span className="chemlab-working-line" key={`${r.formula}-${source.substanceId}`}>
                    <Species formula={r.formula} />
                    <span className="chemlab-working-sum">{sourceLine(source)}</span>
                    <span className="chemlab-working-value">= {formatMoles(source.mol)}</span>
                  </span>
                ))
                : (
                  <span className="chemlab-working-line" key={r.formula}>
                    <Species formula={r.formula} />
                    <span className="chemlab-working-value">{formatMoles(r.mol)}</span>
                  </span>
                )))}
            </dd>

            <dt>Ratio</dt>
            <dd>
              {(s.reagents ?? []).map(r => (
                <span className="chemlab-working-line" key={r.formula}>
                  <Species formula={r.formula} />
                  <span className="chemlab-working-sum">
                    {formatMoles(r.mol)} ÷ {r.coefficient}
                  </span>
                  <span className="chemlab-working-value">
                    = {formatMoles(r.equivalents)}{r.limiting ? ' — the smallest' : ''}
                  </span>
                </span>
              ))}
              <span className="chemlab-working-verdict">
                → <Species formula={(s.reagents ?? []).find(r => r.limiting)?.formula ?? ''} /> is the limiting reagent
              </span>
            </dd>

            {(s.reagents ?? []).some(r => r.leftoverMol > 0) && (
              <>
                <dt>Left over</dt>
                <dd>
                  {(s.reagents ?? []).filter(r => r.leftoverMol > 0).map(r => (
                    <span className="chemlab-working-line" key={r.formula}>
                      <Species formula={r.formula} />
                      <span className="chemlab-working-sum">{formatMoles(r.leftoverMol)}</span>
                      {r.leftoverG !== null && (
                        <span className="chemlab-working-value">= {formatMass(r.leftoverG)}</span>
                      )}
                    </span>
                  ))}
                </dd>
              </>
            )}

            <dt>Produced</dt>
            <dd>
              {(s.products ?? []).map(p => (
                <span className="chemlab-working-line" key={p.formula}>
                  <Species formula={p.formula} />
                  <span className="chemlab-working-sum">{formatMoles(p.mol)} ·</span>
                  {/* One "=" and then the ways of saying the same amount,
                      separated rather than chained: "2.50 mmol = 0.110 g =
                      60.0 mL" reads as a sequence of conversions, which is not
                      what it is. */}
                  <span className="chemlab-working-value">
                    {[
                      p.massG !== null ? formatMass(p.massG) : null,
                      p.volumeL !== null ? `${formatVolume(p.volumeL)} at RTP` : null,
                      p.concentrationM !== null ? `${formatMolarity(p.concentrationM)} in solution` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                </span>
              ))}
            </dd>

            {(s.spectators ?? []).length > 0 && (
              <>
                <dt>Took no part</dt>
                <dd>
                  <span className="chemlab-working-line chemlab-working-spectators">
                    {(s.spectators ?? []).map(nameOf).join(', ')}
                  </span>
                </dd>
              </>
            )}
          </dl>

          {s.excess.band !== 'stoichiometric' && s.excess.ratio !== undefined && (
            <p className="chemlab-working-excess">
              There is {sigFigs(s.excess.ratio)}× as much{' '}
              <Species formula={s.excess.formula ?? ''} /> as the reaction can use.
              {s.excess.band === 'large_excess' && ' Try less of it, or more of the other — the numbers get more interesting near the ratio.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
